/**
 * Z02 · Resultados por zona comercial ORGANIZATIVA (Zona 1/2/3 + Sin zona).
 * NO es geografía (eso es "Resultados por territorio"/CCAA). Misma fuente y
 * cálculo que el YTD Board; cada zona muestra su responsable como etiqueta.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Loader2, Building2, Check, Users } from "lucide-react";
import { MetricInfo } from "@/components/MetricInfo";

const fmtMM = (n) => `${(n || 0).toFixed(2)} M€`;
const fmtPct = (n) => (n == null ? "—" : `${n}%`);
const SMALL_SAMPLE_N = 5;

const DG_OPTS = [
  { v: "", label: "Ambas DG" },
  { v: "SERVICIOS", label: "Urban Services" },
  { v: "TRATAMIENTO", label: "Waste Treatment" },
];

const KpiCard = ({ label, value, sub, color }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`rzc-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <p className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${color || "text-slate-800"}`}>{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

export default function ResultadosPorZona() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minMm, setMinMm] = useState(0);
  const [dg, setDg] = useState("");
  const [selected, setSelected] = useState([]); // códigos de zona; vacío = todas

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("min_mm", minMm);
      if (dg) params.set("dg", dg);
      if (selected.length) params.set("zonas", selected.join(","));
      const { data: d } = await axios.get(`${API}/ytd-board/resultados-por-zona-comercial?${params.toString()}`);
      setData(d);
    } catch (e) {
      toast.error("No se pudo cargar resultados por zona comercial");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [minMm, dg, selected]);

  useEffect(() => { load(); }, [load]);

  const toggleZona = (c) => setSelected((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const total = data?.total;
  const rows = data?.by_zona || [];
  const totOps = total ? Object.values(total.buckets).reduce((s, b) => s + b.volume, 0) : 0;
  const totValue = total ? Object.values(total.buckets).reduce((s, b) => s + b.value_mm, 0) : 0;
  const totWon = total ? (total.buckets.won_signed.volume + total.buckets.won_awarded.volume + total.buckets.won_1st.volume) : 0;
  const catalog = data?.catalog || [];

  return (
    <div className="space-y-5" data-testid="resultados-por-zona-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Building2 size={22} className="text-emerald-600" /> Resultados por zona comercial</h1>
          <p className="text-sm text-slate-500 mt-1">Zonas organizativas (Zona 1/2/3) y su responsable — <b>no es geografía</b>. Para Comunidades Autónomas usa "Resultados por territorio".</p>
        </div>
      </div>

      {/* Filtros (independientes de CCAA) */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Importe mínimo (M€)</label>
          <input type="number" min={0} step={1} value={minMm}
            onChange={(e) => setMinMm(Number(e.target.value) || 0)} data-testid="rzc-min-mm-input"
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-32" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Dirección General</label>
          <select value={dg} onChange={(e) => setDg(e.target.value)} data-testid="rzc-dg-select"
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm">
            {DG_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[240px]">
          <label className="text-xs font-semibold text-slate-500 block mb-1">Zonas (vacío = todas)</label>
          <div className="flex flex-wrap gap-1.5" data-testid="rzc-zona-selector">
            {catalog.map((z) => {
              const active = selected.includes(z.codigo);
              return (
                <button key={z.codigo} onClick={() => toggleZona(z.codigo)} data-testid={`rzc-zona-chip-${z.codigo}`}
                  className={`text-xs px-2.5 py-1 rounded-full border font-medium flex items-center gap-1 transition-colors ${active ? "bg-emerald-600 text-white border-emerald-600" : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
                  {active && <Check size={10} />} {z.nombre_visible} · {z.responsable}
                </button>
              );
            })}
          </div>
        </div>
        {selected.length > 0 && (
          <button onClick={() => setSelected([])} data-testid="rzc-clear" className="text-xs text-emerald-600 hover:underline font-semibold">Limpiar</button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400"><Loader2 className="animate-spin mx-auto mb-2" size={24} />Calculando…</div>
      ) : (
        <>
          {/* Cobertura */}
          {data?.cobertura && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-500 flex items-center gap-2" data-testid="rzc-cobertura">
              <Users size={13} className="text-slate-400" />
              Cobertura de zona: <b className="text-slate-700">{data.cobertura.pct_con_zona ?? "—"}%</b> con zona asignada
              ({data.cobertura.con_zona} de {data.cobertura.total}); {data.cobertura.sin_zona} sin zona asignada (incluidas en el total).
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiCard label="Operaciones" value={totOps} sub={`≥ ${minMm} M€`} />
            <KpiCard label="Valor total" value={fmtMM(totValue)} />
            <KpiCard label="Ganadas" value={totWon} color="text-emerald-600" />
            <KpiCard label="Hit Rate (valor)" value={fmtPct(total?.rates?.hit_rate_value)} color="text-sky-600"
              sub={total?.rates?.hit_rate_n != null ? `N=${total.rates.hit_rate_n} resueltas` : null} />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid="rzc-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2.5">Zona comercial</th>
                  <th className="text-right px-4 py-2.5">Operaciones</th>
                  <th className="text-right px-4 py-2.5">Valor</th>
                  <th className="text-right px-4 py-2.5">Ganadas</th>
                  <th className="text-right px-4 py-2.5">Perdidas</th>
                  <th className="text-right px-4 py-2.5">Pendientes</th>
                  <th className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1 justify-end w-full">
                      Hit Rate
                      <MetricInfo testId="metric-info-hit-rate-zona-comercial" nombre="Éxito firmado por zona comercial"
                        formula="Firmadas / (Firmadas + Perdidas) por importe (M€). Igual que el Hit Rate del YTD Board."
                        poblacion="Contratos ≥ umbral de importe agrupados por zona organizativa (direccion_zona). 'Ganadas' incluye Firmadas+Adjudicadas+1º; solo Firmadas entran en el numerador."
                        unidad="%" corte="Mismo cálculo que YTD Board; no comparable con Presentadas/Adjudicadas de KPIs."
                        filtros="N/D (—) = 0 contratos resueltos (firmadas+perdidas), NO 0%." />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-400" data-testid="rzc-empty">Sin contratos ≥ {minMm} M€ con estos filtros.</td></tr>
                )}
                {rows.map((row) => {
                  const ops = Object.values(row.buckets).reduce((s, b) => s + b.volume, 0);
                  const value = Object.values(row.buckets).reduce((s, b) => s + b.value_mm, 0);
                  const won = row.buckets.won_signed.volume + row.buckets.won_awarded.volume + row.buckets.won_1st.volume;
                  const smallN = row.rates.hit_rate_n != null && row.rates.hit_rate_n > 0 && row.rates.hit_rate_n < SMALL_SAMPLE_N;
                  return (
                    <tr key={row.codigo} className={`border-t border-slate-100 hover:bg-slate-50 ${row.es_sin_zona ? "bg-slate-50/80" : ""}`} data-testid={`rzc-row-${row.codigo}`}>
                      <td className="px-4 py-2.5 font-semibold text-slate-700">
                        <span className={row.es_sin_zona ? "text-slate-500 italic" : ""}>{row.nombre_visible}</span>
                        {row.responsable && <span className="text-xs text-slate-400 font-normal ml-1.5">· {row.responsable}</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right">{ops}</td>
                      <td className="px-4 py-2.5 text-right">{fmtMM(value)}</td>
                      <td className="px-4 py-2.5 text-right text-emerald-600">{won}</td>
                      <td className="px-4 py-2.5 text-right text-rose-600">{row.buckets.lost.volume}</td>
                      <td className="px-4 py-2.5 text-right text-amber-600">{row.buckets.pending_resolution.volume}</td>
                      <td className="px-4 py-2.5 text-right font-bold text-sky-600" data-testid={`rzc-hitrate-${row.codigo}`}>
                        {fmtPct(row.rates.hit_rate_value)}
                        {smallN && (
                          <span className="ml-1.5 text-[10px] font-semibold text-amber-600 align-middle" title={`Muestra pequeña: solo ${row.rates.hit_rate_n} resuelto(s).`} data-testid={`rzc-small-sample-${row.codigo}`}>
                            N={row.rates.hit_rate_n}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
